import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, PartnerPermissionChange } from '../utils/transactionStore';
const FlagChip: React.FC<{
  value: boolean;
}> = ({
  value
}) => <Chip label={value ? 'Y' : 'N'} size="small" sx={{
  bgcolor: value ? 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.12)',
  color: value ? '#059669' : '#6B7280',
  fontWeight: 700,
  fontSize: '0.72rem',
  minWidth: 32
}} data-id="element-704" data-mp="39clk1rb1a1r0" />;
const CheckerPartnerPermissionTable: React.FC = () => {
  const [requests, setRequests] = useState<PartnerPermissionChange[]>([]);
  const [selected, setSelected] = useState<PartnerPermissionChange | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => setRequests(transactionStore.getPendingPartnerPermissionChanges());
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: PartnerPermissionChange, action: 'approve' | 'reject') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'approve') transactionStore.approvePartnerPermissionChange(selected.id, reason);else transactionStore.rejectPartnerPermissionChange(selected.id, reason);
      showToast(`Partner permission change ${actionType === 'approve' ? 'approved' : 'rejected'} for "${selected.partnerName}"`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const rowActions: RowAction<PartnerPermissionChange>[] = [{
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<PartnerPermissionChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-705" data-mp="8ova7bomzjc5">{row.id}</Typography>
  }, {
    id: 'partnerName',
    label: 'Partner',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      maxWidth: 220
    }} data-id="element-706" data-mp="118bwlcjtovnz">{row.partnerName}</Typography>
  }, {
    id: 'txnType',
    label: 'Txn Type',
    renderCell: row => <Chip label={row.txnType} size="small" sx={{
      fontWeight: 700
    }} data-id="element-707" data-mp="2f43n69goj07h" />
  }, {
    id: 'change',
    label: 'Change',
    renderCell: row => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75
    }} data-id="element-708" data-mp="2k3g6ik8cdp5b"><FlagChip value={row.currentAllowed} data-id="element-709" data-mp="2o3qmmppulvxw" /><Icon icon="lucide:arrow-right" width={13} color="#9CA3AF" data-id="element-710" data-mp="2o3grknpy4ylc" /><FlagChip value={row.newAllowed} data-id="element-711" data-mp="2t3usllt4r89v" /></Box>
  }, {
    id: 'effectiveDate',
    label: 'Effective Date',
    renderCell: row => <Chip label={row.effectiveDate} size="small" sx={{
      fontWeight: 700,
      fontSize: '0.7rem',
      bgcolor: 'rgba(91,78,240,0.1)',
      color: '#5B4EF0'
    }} data-id="element-712" data-mp="2y37nmtu5nlnj" />
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 220
    }} data-id="element-713" data-mp="37tai6ygxd7nz">{row.resubmitReason || row.makerReason}</Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-714" data-mp="3gtyyy0xpdm4k">{row.requestedBy}</Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-715" data-mp="385a31qxh1i62">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  return <>
      <DataTable title="Pending Partner Permission Change Requests" data={requests} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending partner permission change requests" initialVisibleColumns={['partnerName', 'txnType', 'change', 'effectiveDate', 'makerReason', 'requestedBy', 'requestedAt']} data-id="element-716" data-mp="334cm1ho76u65" />

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-717" data-mp="2k9fim852lgzb">
        <DialogTitle data-id="element-718" data-mp="2u5iif7cg93j3">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-719" data-mp="2u591sadao8q2">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-720" data-mp="eecpiuqcatir" />
            <Typography variant="h6" fontWeight={600} data-id="element-721" data-mp="9erv8xm479cd">{actionType === 'approve' ? 'Approve' : 'Reject'} Partner Permission Change</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-722" data-mp="lyx7d138ko59">
          <Box sx={{
          mt: 1
        }} data-id="element-723" data-mp="lz6xcooa6vay">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-724" data-mp="7zfmgwt29v16">
                  Partner: <strong data-id="element-725" data-mp="c0r1k8eb6xv4">{selected.partnerName}</strong> &middot; Txn Type: <strong data-id="element-726" data-mp="zpaa8p19tcnq">{selected.txnType}</strong> &middot; Effective: <strong data-id="element-727" data-mp="zptuwdib2f5g">{selected.effectiveDate}</strong>
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-728" data-mp="14nbx7l1thw4h">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-729" data-mp="qxrvmijz34wr">Maker's Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-730" data-mp="ux93tjqsag5m">{selected.makerReason}</Typography>
                </Paper>
                {selected.resubmitReason && <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              borderColor: '#FCD34D'
            }} data-id="element-731" data-mp="1nemjtq0ifmqk">
                    <Typography variant="caption" color="#D97706" fontWeight={600} data-id="element-732" data-mp="1x9tprov2j1m0">Previous Rejection Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-733" data-mp="1t9rm5a5rvy43">{selected.checkerComment}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-734" data-mp="1o8c4ls2t09qg">Maker's Resubmit Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-735" data-mp="1ebzait3cjtv6">{selected.resubmitReason}</Typography>
                  </Paper>}
              </>}
            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this change...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-736" data-mp="19jzg1g61pt5z" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-737" data-mp="qm50skebqlf5">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-738" data-mp="qohn19r47hy0">Cancel</Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-740" data-mp="qhyu90jrdrl8" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-741" data-mp="vfqrma36a5wt" />} data-id="element-739" data-mp="gkkfr6hebcvk">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default CheckerPartnerPermissionTable;